import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useParams, useLocation } from "react-router-dom";
import "./App.css";
import BrandName from './components/BrandName';
import { 
  trackPageView, 
  trackQuoteSubmission, 
  trackOrderCreation, 
  trackAddToCart, 
  trackDesignView,
  trackContactFormSubmission,
  trackServiceRequest,
  trackUserAuth,
  trackDesignerApplication,
  trackDesignUpload,
  loadMetaPixel,
  trackPixelPageView
} from './utils/analytics';
import { Upload, FileText, Zap, Settings, ChevronRight, ChevronDown, Check, CheckCircle, Mail, Phone, MapPin, ArrowLeft, User, Users, LogIn, ShoppingCart, Package, Eye, Edit, X } from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./components/ui/tabs";
import { Label } from "./components/ui/label";
import { Badge } from "./components/ui/badge";
import { Separator } from "./components/ui/separator";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { CurrencyProvider, useCurrency } from "./contexts/CurrencyContext";
import { CartProvider, useCart } from "./contexts/CartContext";
import Profile from "./components/Profile";
import LoginModal from "./components/LoginModal";
import MainNavigation from "./components/MainNavigation";
import ProductsPage from "./components/ProductsPage";
import Footer from "./components/Footer";
import ProductDetailPage from "./components/ProductDetailPage";
import AdminPanel from "./components/AdminPanel";
import CartPage from "./components/CartPage";
import ThreeDPreview from "./components/ThreeDPreview";
import SharedPageLayout from "./components/SharedPageLayout";
import ComingSoon from "./components/ComingSoon";
import CurrencySelector from "./components/CurrencySelector";
import FloatingCart from "./components/FloatingCart";
import { BACKEND_URL } from "./config";
import { termsOfService, shippingReturns, licensingPolicy, privacyPolicy } from "./data/policies";
import { TermsOfServiceRoute, ShippingReturnsRoute, LicensingPolicyRoute, PrivacyPolicyRoute } from "./pages/PolicyPages";
import { FAQRoute, TermsRoute, FileFormatsRoute, MaterialGuideRoute, QualityStandardsRoute, ShippingInfoRoute } from "./pages/InfoPages";
import { ProductsRoute, ProductDetailRoute, CartRoute, ProfileRoute, AdminRoute, AboutRoute } from "./pages/SimpleRoutes";
import { ServicesRoute, ContactRoute } from "./pages/FeatureRoutes";
import { AdminMarketplaceRoute, MarketplaceRoute, ListingDetailRoute, DesignerEditRoute } from "./pages/MarketplaceRoutes";
import { DesignerApplyRoute, DesignerDashboardRoute, DesignerUploadRoute, DesignerTermsRoute, CustomerLicenseTermsRoute } from "./pages/DesignerRoutes";
import { ComingSoonPage, AppContent, CreatorProfileRoute } from "./pages/HomePage";
import DesignerStorefront from "./pages/DesignerStorefront";
import ProfileQuickActionMockups from "./pages/ProfileQuickActionMockups";
import DesignerInfoPage from "./pages/DesignerInfoPage";
import ServicesToolPage from "./pages/ServicesToolPage";
import InstantQuoteLanding from "./pages/InstantQuoteLanding";
import CustomDesignLanding from "./pages/CustomDesignLanding";

// Use runtime-detected backend URL (handles different deployment environments)
const API_BASE = BACKEND_URL;
const API = `${API_BASE}/api`;

// Google Analytics Page View Tracker
// Also sets a per-route document.title + meta description + canonical URL so:
//   1. GA4's default "Pages and screens" report (which groups by page_title)
//      shows meaningful per-page rows
//   2. Search engines see unique meta descriptions per route (better SERP
//      snippets and click-through)
//   3. Each route declares its canonical URL — avoids duplicate-content
//      penalties when the same page is reachable from query-string variants.
const BASE_TITLE = 'Create 3D Works';
const SITE_URL = 'https://create3dworks.com';
// Default description fallback (matches index.html).
const DEFAULT_DESCRIPTION = 'Professional on-demand 3D printing and custom design services. Upload your CAD file for an instant quote, or work with our engineering team to bring an idea to life.';

// Per-route Open Graph images. Hosted on our own domain so neither the image
// URLs nor any of the meta tags leak third-party platform names. The default
// below is the same image hardcoded in index.html for first-paint fallback.
const OG_IMAGE_HOME           = '/og/home.png';
const OG_IMAGE_INSTANT_QUOTE  = '/og/instant-quote.png';
const OG_IMAGE_CUSTOM_DESIGN  = '/og/custom-design.png';
const OG_IMAGE_DEFAULT        = '/og/default.png';

// Format: [title, description, ogImageOverride?]. Routes without an explicit
// image fall back to OG_IMAGE_DEFAULT.
const ROUTE_META = {
  '/':                 ['Create 3D Works — Professional 3D Printing Services',
                        'Professional on-demand 3D printing and custom design. Upload your CAD file for an instant quote in seconds, with engineering-grade materials and finishes.',
                        OG_IMAGE_HOME],
  '/about':            [`About — ${BASE_TITLE}`,
                        'Meet the engineers behind Create 3D Works. We print, finish, and ship every part ourselves — no middlemen. Learn what drives our quality-first approach to 3D printing.'],
  '/services':         [`Services — ${BASE_TITLE}`,
                        'Explore our full range of 3D printing and design services: instant quotes, custom CAD design, 2D-to-3D conversion, file optimization, and photorealistic renders.'],
  '/contact':          [`Contact — ${BASE_TITLE}`,
                        'Get in touch with the Create 3D Works team. We typically respond within one business day. Use our contact form for project enquiries, support, or partnership questions.'],
  '/faq':              [`FAQ — ${BASE_TITLE}`,
                        'Frequently asked questions about 3D printing materials, finishes, lead times, file formats, shipping, returns, and intellectual property at Create 3D Works.'],
  '/products':         [`Shop — ${BASE_TITLE}`,
                        'Browse our curated catalog of 3D printers, filaments, and accessories — hand-picked by working engineers. Free shipping on orders over $75.'],
  '/cart':             [`Your Cart — ${BASE_TITLE}`,
                        'Review the items in your cart and check out securely.'],
  '/profile':          [`Your Profile — ${BASE_TITLE}`,
                        'Manage your account, orders, saved quotes, addresses, and design library.'],
  '/admin':            [`Admin — ${BASE_TITLE}`,
                        'Administrator dashboard.'],
  '/marketplace':      [`Designs Marketplace — ${BASE_TITLE}`,
                        'Browse and download print-ready designs from our community of independent designers. License for personal use or commercial production, or have us print them for you.'],
  '/instant-quote':    [`Instant 3D Print Quote — ${BASE_TITLE}`,
                        'Upload your CAD file (STL, STEP, 3MF, OBJ and more) and get an instant 3D printing quote. Choose material, finish, color, and quantity — see price and lead time in seconds.',
                        OG_IMAGE_INSTANT_QUOTE],
  '/custom-design':    [`Custom 3D Design Service — ${BASE_TITLE}`,
                        'Have an idea but no CAD file? Our engineering team designs your part from a sketch, photo, or napkin idea. Full IP ownership, fast turnaround, dedicated engineer.',
                        OG_IMAGE_CUSTOM_DESIGN],
  '/get-started':      [`Get Started — ${BASE_TITLE}`,
                        'Pick the service that fits your project: instant 3D print quote, custom CAD design, 2D-to-3D conversion, file optimization, or photorealistic renders.'],
  '/terms':            [`Terms of Service — ${BASE_TITLE}`,            'Read our terms of service for using Create 3D Works.'],
  '/terms-of-service': [`Terms of Service — ${BASE_TITLE}`,            'Read our terms of service for using Create 3D Works.'],
  '/privacy':          [`Privacy Policy — ${BASE_TITLE}`,              'Our privacy policy: how we collect, use, and protect your data.'],
  '/privacy-policy':   [`Privacy Policy — ${BASE_TITLE}`,              'Our privacy policy: how we collect, use, and protect your data.'],
  '/designer/info':    [`Become a Designer — ${BASE_TITLE}`,           'Earn royalties by selling your 3D designs on Create 3D Works. Set your own license terms, keep IP rights, and reach print-ready buyers worldwide.'],
  '/designer/apply':   [`Designer Application — ${BASE_TITLE}`,        'Apply to become a Create 3D Works designer.'],
  '/designer/dashboard': [`Designer Dashboard — ${BASE_TITLE}`,        'Manage your designs, earnings, and uploads.'],
  '/designer/upload':  [`Upload Design — ${BASE_TITLE}`,               'Upload a new design to the Create 3D Works marketplace.'],
  '/designer/terms':   [`Designer Terms — ${BASE_TITLE}`,              'Terms and conditions for designers on Create 3D Works.'],
};

const titleForPath = (pathname) => {
  if (ROUTE_META[pathname]) return ROUTE_META[pathname][0];

  // Dynamic patterns
  if (pathname.startsWith('/start/')) {
    const slug = pathname.replace('/start/', '').replace(/-/g, ' ');
    return `${slug.charAt(0).toUpperCase() + slug.slice(1)} — ${BASE_TITLE}`;
  }
  if (pathname.startsWith('/product/')) return `Product — ${BASE_TITLE}`;
  if (pathname.startsWith('/marketplace/listing/')) return `Design — ${BASE_TITLE}`;
  if (pathname.startsWith('/creator/') || pathname.startsWith('/designer/')) return `Designer — ${BASE_TITLE}`;
  if (pathname.startsWith('/admin/')) return `Admin — ${BASE_TITLE}`;
  return BASE_TITLE;
};

const descriptionForPath = (pathname) => {
  if (ROUTE_META[pathname]) return ROUTE_META[pathname][1];
  return DEFAULT_DESCRIPTION;
};

const ogImageForPath = (pathname) => {
  // Explicit per-route override (slot 2) when present, otherwise default
  if (ROUTE_META[pathname] && ROUTE_META[pathname][2]) return ROUTE_META[pathname][2];
  return OG_IMAGE_DEFAULT;
};

// Set or create a <meta> tag in document.head. Handles both `name=` and `property=`.
const setMetaTag = (attr, key, value) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', value);
};

const setCanonicalUrl = (url) => {
  let link = document.head.querySelector('link[rel="canonical"]');
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    document.head.appendChild(link);
  }
  link.setAttribute('href', url);
};

const PageViewTracker = () => {
  const location = useLocation();

  useEffect(() => {
    const newTitle = titleForPath(location.pathname);
    const newDescription = descriptionForPath(location.pathname);
    const canonicalUrl = `${SITE_URL}${location.pathname}`;
    const ogImage = ogImageForPath(location.pathname);
    // Crawlers (FB/LinkedIn) require an absolute URL — prefix relative og paths.
    const ogImageAbs = ogImage.startsWith('http') ? ogImage : `${SITE_URL}${ogImage}`;

    if (document.title !== newTitle) document.title = newTitle;
    setMetaTag('name', 'description', newDescription);
    // Mirror to Open Graph + Twitter so social-share cards reflect the route
    setMetaTag('property', 'og:title', newTitle);
    setMetaTag('property', 'og:description', newDescription);
    setMetaTag('property', 'og:url', canonicalUrl);
    setMetaTag('property', 'og:image', ogImageAbs);
    setMetaTag('name', 'twitter:title', newTitle);
    setMetaTag('name', 'twitter:description', newDescription);
    setMetaTag('name', 'twitter:image', ogImageAbs);
    setCanonicalUrl(canonicalUrl);

    // GA4 pageview with correct path + title
    trackPageView(location.pathname + location.search, newTitle);
    // Mirror the same page view to Meta Pixel (fires only if pixel is loaded)
    trackPixelPageView();
  }, [location]);

  return null; // This component doesn't render anything
};

// One-shot Meta Pixel loader. Reads the public tracking-settings endpoint on
// mount and injects fbevents.js when a pixel ID is configured by an admin.
const MetaPixelLoader = () => {
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/site/tracking-settings`);
        if (!res.ok) return;
        const data = await res.json();
        const pixelId = (data && data.meta_pixel_id) || '';
        if (!cancelled && pixelId) {
          loadMetaPixel(pixelId);
        }
      } catch (e) {
        // Tracking is best-effort — never block the app on a failed fetch
      }
    })();
    return () => { cancelled = true; };
  }, []);
  return null;
};

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    // Check if coming soon mode is enabled
    const comingSoonEnabled = process.env.REACT_APP_COMING_SOON === 'false';
    if (!comingSoonEnabled) return true; // If not in coming soon mode, allow access
    
    // Check if already authenticated (persists across sessions)
    return localStorage.getItem('site_authenticated') === 'true';
  });

  const handleAuthenticate = () => {
    setIsAuthenticated(true);
  };

  // Check if coming soon mode is enabled
  const comingSoonEnabled = process.env.REACT_APP_COMING_SOON === 'true';

  // If coming soon is enabled and user is not authenticated, show password page
  if (comingSoonEnabled && !isAuthenticated) {
    return <ComingSoon onAuthenticated={handleAuthenticate} />;
  }

  // Otherwise, show the full app
  return (
    <Router>
      <PageViewTracker />
      <MetaPixelLoader />
      <AuthProvider>
        <CurrencyProvider>
          <CartProvider>
            <CurrencySelector />
            <FloatingCart />
            <Routes>
            <Route path="/" element={<AppContent />} />
            <Route path="/get-started" element={<ServicesToolPage />} />
            {/* Clean per-service URLs for Google Ads landing pages — each slug
                deep-links straight into one wizard so ads can track per-service
                traffic without query-string noise. */}
            <Route path="/start/:slug" element={<ServicesToolPage />} />
            <Route path="/instant-quote" element={<InstantQuoteLanding />} />
            <Route path="/custom-design" element={<CustomDesignLanding />} />
            <Route path="/products" element={<ProductsRoute />} />
            <Route path="/product/:slug" element={<ProductDetailRoute />} />
            <Route path="/cart" element={<CartRoute />} />
            <Route path="/profile" element={<ProfileRoute />} />
            <Route path="/admin" element={<AdminRoute />} />
            <Route path="/about" element={<AboutRoute />} />
            <Route path="/services" element={<ServicesRoute />} />
            <Route path="/contact" element={<ContactRoute />} />
            <Route path="/marketplace" element={<MarketplaceRoute />} />
            <Route path="/marketplace/listing/:id" element={<ListingDetailRoute />} />
            <Route path="/creator/:username" element={<DesignerStorefront />} />
            <Route path="/profile-quick-action-mockups" element={<ProfileQuickActionMockups />} />
            <Route path="/designer/:username" element={<DesignerStorefront />} />
            <Route path="/designer/info" element={<DesignerInfoPage />} />
            <Route path="/designer/apply" element={<DesignerApplyRoute />} />
            <Route path="/designer/dashboard" element={<DesignerDashboardRoute />} />
            <Route path="/designer/upload" element={<DesignerUploadRoute />} />
            <Route path="/designer/edit/:id" element={<DesignerEditRoute />} />
            <Route path="/designer/terms" element={<DesignerTermsRoute />} />
            <Route path="/customer/license-terms" element={<CustomerLicenseTermsRoute />} />
            <Route path="/admin/marketplace" element={<AdminMarketplaceRoute />} />
            <Route path="/faq" element={<FAQRoute />} />
            <Route path="/terms" element={<TermsRoute />} />
            <Route path="/file-formats" element={<FileFormatsRoute />} />
            <Route path="/material-guide" element={<MaterialGuideRoute />} />
            <Route path="/quality-standards" element={<QualityStandardsRoute />} />
            <Route path="/shipping-info" element={<ShippingInfoRoute />} />
            
            {/* Policy Pages */}
            <Route path="/terms-of-service" element={<TermsOfServiceRoute />} />
            <Route path="/shipping-returns" element={<ShippingReturnsRoute />} />
            <Route path="/licensing-policy" element={<LicensingPolicyRoute />} />
            <Route path="/privacy-policy" element={<PrivacyPolicyRoute />} />
            <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </CartProvider>
        </CurrencyProvider>
      </AuthProvider>
    </Router>
  );
};

// Utility function to create SEO-friendly slugs
const createProductSlug = (manufacturer, productName) => {
  const combined = `${manufacturer} ${productName}`;
  return combined
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .trim('-'); // Remove leading/trailing hyphens
};

// Utility function to parse slug back to search terms
const parseProductSlug = (slug) => {
  return slug
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Cart Context moved to /contexts/CartContext.js
// HomePage components (ComingSoonPage, AppContent, CreatorProfileRoute) moved to /pages/HomePage.js
// Designer routes (Apply, Dashboard, Upload, Terms, CustomerLicenseTerms) moved to /pages/DesignerRoutes.js

export default App;
